import React from 'react';
import { Modal, ModalProps } from '@odin-labs/components';
import {
  CardPrintJobStatus,
  CardStatus,
  WorkerCardType,
} from 'apollo/generated/client-operations';
import { CheckIcon, RedoIcon } from 'components/icons';
import { LoadingError } from 'components/loadingError';
import { BadgingStep, CardPrintJob } from 'containers/worker/types';
import {
  AssignBadgeModalContent,
  AssignQRModalContent,
  AssignTempBadgeModalContent,
  BadgeType,
  ChooseBadgeTypeModalContent,
  PrintBadgeModalContent,
  CreateCardPrintJobModalContent,
  DeactivateBadgesModalContent,
  EnterPrintJobCardNumberModalContent,
  ReactivateBadgeModalContent,
  ReassignBadgeModalContent,
  ReassignBadgeState,
  ReassignQRModalContent,
  IssueFacialRecognitionBadgeModalContent,
  IssueMobileBadgeModalContent,
} from './components';
import { BadgeModalProps, ReactivateBadgeState } from './types';
import { AssignQRFormData } from './components/assignQRModalContent/types';
import { BadgeTypeOption } from './components/chooseBadgeTypeModalContent/types';

const isCardPrintJobUnread = (cardPrintJob: CardPrintJob): boolean =>
  cardPrintJob?.status === CardPrintJobStatus.Printed &&
  cardPrintJob?.workerCard == null;

const isCardPrintJobComplete = (cardPrintJob: CardPrintJob): boolean =>
  cardPrintJob?.status === CardPrintJobStatus.Printed &&
  cardPrintJob?.workerCard != null;

export function BadgeModal(props: BadgeModalProps): React.ReactElement {
  const {
    isOpen,
    badgingStep,
    prevBadgingStep,
    worker,
    jobsites,
    jobsiteWorkers,
    workerCards = [],
    workerCardToReactivate,
    workerCardToReprint,
    expirationDate,
    currentCardPrintJob = null,
    setCurrentCardPrintJob,
    refetchWorkerCards,
    refetchCurrentCardPrintJob,
    closeModal,
    updateBadgeModalState,
    updateWorkerState,
  } = props;

  const [jobsiteId, setJobsiteId] = React.useState<string>(null);
  const jobsiteName = React.useMemo(() => {
    return jobsites?.find((j) => j.jobsiteId === jobsiteId)?.name;
  }, [jobsiteId, jobsites]);
  const [workerCardFormatId, setWorkerCardFormatId] =
    React.useState<string>(null);
  const [reassignBadgeState, setReassignBadgeState] =
    React.useState<ReassignBadgeState>(null);
  const [reassignQRData, setReassignQRData] =
    React.useState<AssignQRFormData>(null);

  const badgesToDeactivate = React.useMemo(
    () =>
      workerCards?.filter(
        (workerCard) =>
          workerCard.workerCardId !==
            currentCardPrintJob?.workerCard?.workerCardId &&
          workerCard.cardStatus === CardStatus.Active &&
          workerCard.workerCardFormat.cardType === WorkerCardType.Proximity,
      ) ?? [],
    [workerCards, currentCardPrintJob],
  );

  const qrCodesToDeactivate = React.useMemo(
    () =>
      badgingStep === BadgingStep.QRCodeDeactivationPrompt
        ? workerCards?.filter(
            (workerCard) =>
              workerCard.cardStatus === CardStatus.Active &&
              workerCard.workerCardFormat.cardType === WorkerCardType.QrCode,
          )
        : undefined,
    [workerCards, badgingStep],
  );

  const selectedJobsiteWorker = React.useMemo(() => {
    if (!jobsiteId) return null;
    return (
      jobsiteWorkers?.find(
        (jw) => jw?.jobsiteContractor?.jobsite?.jobsiteId === jobsiteId,
      ) ?? null
    );
  }, [jobsiteId, jobsiteWorkers]);

  const isBannedOnSelectedJobsite =
    !!selectedJobsiteWorker?.isBanned && !selectedJobsiteWorker?.banExpiresAt;
  const isSuspendedOnSelectedJobsite =
    !!selectedJobsiteWorker?.isBanned &&
    Boolean(selectedJobsiteWorker?.banExpiresAt);
  const suspensionDate = selectedJobsiteWorker?.banExpiresAt ?? null;

  React.useEffect(() => {
    if (currentCardPrintJob && badgingStep === BadgingStep.Printing) {
      if (isCardPrintJobComplete(currentCardPrintJob)) {
        const nextBadgingStep =
          currentCardPrintJob.workerCard?.workerCardFormat?.cardType ===
          WorkerCardType.Pvc
            ? BadgingStep.ConfirmFinished
            : BadgingStep.BadgeCreated;
        updateBadgeModalState({ badgingStep: nextBadgingStep });
      } else if (isCardPrintJobUnread(currentCardPrintJob)) {
        updateBadgeModalState({
          badgingStep: BadgingStep.PrintCompletedNoCardNumber,
        });
      } else if (currentCardPrintJob.status === CardPrintJobStatus.Failed) {
        const printJobWorkerCard = currentCardPrintJob.workerCard;
        updateBadgeModalState({
          badgingStep: BadgingStep.Failed,
          workerCardToReprint: printJobWorkerCard
            ? {
                id: printJobWorkerCard.workerCardId,
                cardType: printJobWorkerCard.workerCardFormat?.cardType,
              }
            : null,
        });
      }
    }
  }, [currentCardPrintJob, badgingStep]);

  React.useEffect(() => {
    if (badgingStep === BadgingStep.BadgeCreated) {
      const newBadgingStep = badgesToDeactivate.length
        ? BadgingStep.ProximityCardDeactivationPrompt
        : BadgingStep.ConfirmFinished;
      updateBadgeModalState({ badgingStep: newBadgingStep });
    }
  }, [badgingStep, badgesToDeactivate]);

  const reactivateBadge = (
    reactivateBadgeState: ReactivateBadgeState,
  ): void => {
    updateBadgeModalState({
      badgingStep: BadgingStep.ReactivateBadge,
      ...reactivateBadgeState,
    });
  };

  const getModalProps = (): Omit<ModalProps, 'open' | 'setOpen'> => {
    switch (badgingStep) {
      case BadgingStep.ChooseBadgeType:
        return {
          actionsPanel: null,
          title: 'Add new badge',
          children: (
            <ChooseBadgeTypeModalContent
              setOpen={closeModal}
              onAction={(
                selectedJobsiteId: string,
                selectedBadgeTypeOption: BadgeTypeOption,
              ): void => {
                setJobsiteId(selectedJobsiteId);
                setWorkerCardFormatId(
                  selectedBadgeTypeOption.workerCardFormatId,
                );
                switch (selectedBadgeTypeOption.value) {
                  case BadgeType.FacialRecognition:
                    updateBadgeModalState({
                      badgingStep: BadgingStep.IssueFacialEnrollment,
                    });
                    break;
                  case BadgeType.MobileBadge:
                    updateBadgeModalState({
                      badgingStep: BadgingStep.IssueMobileBadge,
                    });
                    break;
                  case BadgeType.QrCode:
                    updateBadgeModalState({
                      badgingStep: BadgingStep.AssignQRCode,
                    });
                    break;
                  case BadgeType.ExistingBadge:
                    updateBadgeModalState({
                      badgingStep: BadgingStep.AssignExistingBadge,
                    });
                    break;
                  case BadgeType.TempBadge:
                    updateBadgeModalState({
                      badgingStep: BadgingStep.AssignTempBadge,
                    });
                    break;
                  case BadgeType.NewBadge:
                    updateBadgeModalState({
                      badgingStep: BadgingStep.CreatePrintJob,
                    });
                    break;
                  case BadgeType.NewIDCard:
                    updateBadgeModalState({
                      badgingStep: BadgingStep.Print,
                    });
                    break;
                  default:
                    break;
                }
              }}
              jobsites={jobsites}
            />
          ),
        };
      case BadgingStep.IssueMobileBadge:
        return {
          actionsPanel: null,
          title: 'Issue mobile badge',
          children: (
            <IssueMobileBadgeModalContent
              setOpen={closeModal}
              onAction={(): void => {
                closeModal();
                refetchWorkerCards();
              }}
              jobsiteId={jobsiteId}
              workerId={worker.workerId}
              workerCardFormatId={workerCardFormatId}
              workerCards={workerCards}
              worker={worker}
            />
          ),
        };
      case BadgingStep.IssueFacialEnrollment:
        return {
          actionsPanel: null,
          title: 'Start facial recognition enrollment',
          children: (
            <IssueFacialRecognitionBadgeModalContent
              setOpen={closeModal}
              onAction={(): void => {
                closeModal();
                refetchWorkerCards();
              }}
              refetchWorkerCards={refetchWorkerCards}
              jobsiteId={jobsiteId}
              workerId={worker.workerId}
              workerCardFormatId={workerCardFormatId}
              workerCards={workerCards}
              worker={worker}
            />
          ),
        };
      case BadgingStep.AssignQRCode:
        return {
          actionsPanel: null,
          title: 'Assign a QR code sticker',
          children: (
            <AssignQRModalContent
              setOpen={closeModal}
              onAction={(assignQRData?: AssignQRFormData): void => {
                setReassignQRData(assignQRData);
                if (assignQRData) {
                  updateBadgeModalState({
                    badgingStep: BadgingStep.ReassignQRCode,
                  });
                } else {
                  closeModal();
                  refetchWorkerCards();
                }
              }}
              reactivateBadge={reactivateBadge}
              jobsiteId={jobsiteId}
              workerId={worker.workerId}
              workerCards={workerCards}
              workerCardFormatId={workerCardFormatId}
            />
          ),
        };
      case BadgingStep.ReassignQRCode:
        return {
          actionsPanel: null,
          title: 'This QR code is currently assigned to another worker',
          subtitle: 'Would you like to reassign this QR code to this worker?',
          children: (
            <ReassignQRModalContent
              setOpen={(): void => {
                setReassignQRData(null);
                closeModal();
              }}
              onAction={(): void => {
                setReassignQRData(null);
                closeModal();
                refetchWorkerCards();
              }}
              jobsiteId={jobsiteId}
              workerId={worker.workerId}
              assignQRData={reassignQRData}
              workerCardFormatId={workerCardFormatId}
            />
          ),
        };
      case BadgingStep.AssignExistingBadge:
        return {
          actionsPanel: null,
          title: 'Register a permanent badge',
          children: (
            <AssignBadgeModalContent
              setOpen={closeModal}
              onAction={(reassignState): void => {
                setReassignBadgeState(reassignState);
                if (reassignState) {
                  updateBadgeModalState({
                    badgingStep: BadgingStep.ReassignExistingBadge,
                  });
                } else {
                  closeModal();
                  refetchWorkerCards();
                }
              }}
              reactivateBadge={reactivateBadge}
              jobsiteId={jobsiteId}
              workerId={worker.workerId}
              workerCards={workerCards}
            />
          ),
        };
      case BadgingStep.AssignTempBadge:
        return {
          actionsPanel: null,
          title: 'Assign a temporary badge',
          children: (
            <AssignTempBadgeModalContent
              setOpen={closeModal}
              onAction={(reassignState): void => {
                setReassignBadgeState(reassignState);
                if (reassignState) {
                  updateBadgeModalState({
                    badgingStep: BadgingStep.ReassignExistingBadge,
                  });
                } else {
                  closeModal();
                  refetchWorkerCards();
                }
              }}
              reactivateBadge={reactivateBadge}
              jobsiteId={jobsiteId}
              jobsiteName={jobsiteName}
              workerId={worker.workerId}
              workerCards={workerCards}
              isBannedOnSelectedJobsite={isBannedOnSelectedJobsite}
              isSuspendedOnSelectedJobsite={isSuspendedOnSelectedJobsite}
              suspensionDate={suspensionDate}
            />
          ),
        };
      case BadgingStep.ReassignExistingBadge:
        return {
          actionsPanel: null,
          title: `This badge is currently assigned to the following worker${
            reassignBadgeState?.workerCardInfo?.length > 1 ? 's' : ''
          }:`,
          children: (
            <ReassignBadgeModalContent
              setOpen={(): void => {
                setReassignBadgeState(null);
                closeModal();
              }}
              onAction={(): void => {
                setReassignBadgeState(null);
                refetchWorkerCards();
                if (
                  prevBadgingStep === BadgingStep.PrintCompletedNoCardNumber
                ) {
                  updateBadgeModalState({
                    badgingStep: BadgingStep.BadgeCreated,
                  });
                } else {
                  closeModal();
                }
              }}
              jobsiteId={jobsiteId}
              reassignBadgeState={reassignBadgeState}
            />
          ),
        };
      case BadgingStep.Print:
        return {
          actionsPanel: null,
          title: `${workerCardToReprint ? 'Reprint' : 'Print'} an ID card`,
          children: (
            <PrintBadgeModalContent
              setOpen={closeModal}
              onAction={(cardPrintJob: CardPrintJob): void => {
                setCurrentCardPrintJob(cardPrintJob);
                updateBadgeModalState({ badgingStep: BadgingStep.Printing });
                refetchWorkerCards();
              }}
              worker={worker}
              workerCardIdToReprint={workerCardToReprint?.id}
              jobsiteId={
                prevBadgingStep === BadgingStep.ChooseBadgeType
                  ? jobsiteId
                  : currentCardPrintJob?.jobsite?.jobsiteId
              }
              updateWorkerState={updateWorkerState}
            />
          ),
        };
      case BadgingStep.CreatePrintJob:
        return {
          actionsPanel: null,
          title: 'Print a permanent badge',
          children: (
            <CreateCardPrintJobModalContent
              setOpen={closeModal}
              onAction={(cardPrintJob: CardPrintJob): void => {
                setCurrentCardPrintJob(cardPrintJob);
                updateBadgeModalState({ badgingStep: BadgingStep.Printing });
              }}
              worker={worker}
              jobsiteId={
                prevBadgingStep === BadgingStep.ChooseBadgeType
                  ? jobsiteId
                  : currentCardPrintJob?.jobsite?.jobsiteId
              }
              updateWorkerState={updateWorkerState}
            />
          ),
        };
      case BadgingStep.Printing:
        return {
          title: 'Badge printing...',
          subtitle: `Status: ${
            currentCardPrintJob?.statusMessage ?? currentCardPrintJob?.status
          }`,
          size: 'sm',
          cancelText: 'Close',
          onAction: (): void => {
            const isPvcCard =
              currentCardPrintJob?.workerCard?.workerCardFormat?.cardType ===
              WorkerCardType.Pvc;
            updateBadgeModalState({
              badgingStep: isPvcCard
                ? BadgingStep.Print
                : BadgingStep.CreatePrintJob,
            });
          },
          actionText: 'New print job',
          actionIcon: RedoIcon,
          children: <LoadingError noPadding loading />,
        };
      case BadgingStep.PrintCompletedNoCardNumber:
        return {
          actionsPanel: null,
          title: 'Register a permanent badge',
          children: (
            <EnterPrintJobCardNumberModalContent
              setOpen={closeModal}
              onAction={(reassignState): void => {
                setReassignBadgeState(reassignState);
                if (reassignState) {
                  updateBadgeModalState({
                    badgingStep: BadgingStep.ReassignExistingBadge,
                  });
                } else {
                  refetchWorkerCards();
                  updateBadgeModalState({
                    badgingStep: BadgingStep.BadgeCreated,
                  });
                }
              }}
              cardPrintJob={currentCardPrintJob}
              refetchCardPrintJob={refetchCurrentCardPrintJob}
              jobsiteId={jobsiteId}
            />
          ),
        };
      case BadgingStep.ReactivateBadge:
        return {
          actionsPanel: null,
          title: 'Reactivate badge',
          subtitle: 'Would you like to reactivate this badge?',
          children: (
            <ReactivateBadgeModalContent
              setOpen={closeModal}
              onAction={(reassignState): void => {
                setReassignBadgeState(reassignState);
                if (reassignState) {
                  updateBadgeModalState({
                    badgingStep: BadgingStep.ReassignExistingBadge,
                  });
                } else {
                  closeModal();
                  refetchWorkerCards();
                }
              }}
              workerCardIdToReactivate={workerCardToReactivate?.id}
              expirationDate={expirationDate}
              workerCards={
                workerCardToReactivate?.cardType !== WorkerCardType.Pvc
                  ? workerCards
                  : null
              }
            />
          ),
        };
      case BadgingStep.ProximityCardDeactivationPrompt:
        return {
          actionsPanel: null,
          title: 'The badge has been assigned to this worker',
          children: (
            <DeactivateBadgesModalContent
              setOpen={closeModal}
              onAction={(): void => {
                closeModal();
                refetchWorkerCards();
              }}
              workerCards={badgesToDeactivate}
              workerCardType={WorkerCardType.Proximity}
            />
          ),
        };
      case BadgingStep.QRCodeDeactivationPrompt:
        return {
          actionsPanel: null,
          title: 'The QR code has been assigned to this worker',
          children: (
            <DeactivateBadgesModalContent
              setOpen={closeModal}
              onAction={(): void => {
                closeModal();
                refetchWorkerCards();
              }}
              workerCards={qrCodesToDeactivate}
              workerCardType={WorkerCardType.QrCode}
            />
          ),
        };
      case BadgingStep.ConfirmFinished:
        return {
          title: 'The badge has been assigned to this worker',
          size: 'sm',
          onAction: (): void => {
            closeModal();
            refetchWorkerCards();
          },
          actionText: 'Finish',
          actionIcon: CheckIcon,
        };
      case BadgingStep.Failed:
        return {
          size: 'sm',
          title: 'Your badge failed to print',
          onAction: (): void => {
            const isPvcCard =
              currentCardPrintJob?.workerCard?.workerCardFormat?.cardType ===
              WorkerCardType.Pvc;
            updateBadgeModalState({
              badgingStep: isPvcCard
                ? BadgingStep.Print
                : BadgingStep.CreatePrintJob,
            });
          },
          actionText: 'Try again',
          actionIcon: RedoIcon,
          children: (
            <p>
              The printer returned the following error:{' '}
              {currentCardPrintJob?.statusMessage}
            </p>
          ),
        };
      default:
        return {};
    }
  };

  const modalProps = getModalProps();

  return <Modal open={isOpen} setOpen={closeModal} size="md" {...modalProps} />;
}
